import AutorenewIcon from '@mui/icons-material/Autorenew';
import ClearIcon from '@mui/icons-material/Clear';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import { TabContext, TabPanel } from '@mui/lab';
import { Button, Container, FormControl, Grid, IconButton, InputLabel, LinearProgress, LinearProgressProps, MenuItem, Select, Stack, Step, StepLabel, Tab, Tabs, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import {
  DataGridProProps,
  GridColumns,
  GridRenderCellParams,
  GridValueFormatterParams,
  useGridApiRef
} from '@mui/x-data-grid-pro';

import QuickBooksLogo from '../../../assets/images/logos/qb-logo.svg';
import HorizontalBox from '../../../components/Boxes/HorizontalBox/HorizontalBox';
import StyledButton from "../../../components/Buttons/Button/Button";
import DetailPanel from '../../../components/DetailPanel/DetailPanel';
import { StyledBackdrop } from '../../../components/DetailPanel/DetailPanel.styles';
import ConfirmationDialog from '../../../components/Modal/ConfirmationDialog';
import FullScreenModal from '../../../components/Modal/FullScreenModal/FullScreenModal';
import { SaveExitModal } from '../../../components/Modal/SaveExitModal/SaveExitModal';
import { PageLock } from '../../../components/PageLock/PageLock';
import ProgressModal from '../../../components/Progress/ProgressModal/ProgressModal';
import ProgressPanel from '../../../components/Progress/ProgressPanel/ProgressPanel';
import { DateTimeFormat } from '../../../utils/helpers/format.helper';
import NewTransactionModal from './NewTransactionModal/NewTransactionModal';
import { useTransactionMapperEffect } from './TransactionMapper.hooks';
import { BulkMappingWrapper, DisconnectButton, GLAccount, GLAccountChild, HeaderRow, PanelBox, PanelDescription, PlatformButton, SelectBox, SelectMenuProps, Stepper, StyledDataGridPro, StyledIconButton, StyledProgress, StyledTextfield, StyledTooltip, TypographyBold } from './TransactionMapper.styles';

function LinearProgressWithLabel(props: LinearProgressProps & { value: number, total: number }) {
  const {
    total, value
  } = props;

  return (
    <StyledProgress>
      <Typography variant="body1">{total} Accounts</Typography>
      <LinearProgress id="progress_percent_mapped" variant="determinate" {...props} />
      <Typography variant="body1">{`${Math.round(value,)}% mapped`}</Typography>
    </StyledProgress>
  );
}

interface SearchBarProps {
  clearSearch: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value: string;
}

function SearchBar(props: SearchBarProps) {
  const {
    value, onChange, clearSearch
  } = props;

  return (
    <FormControl size="small">
      <StyledTextfield
        id="txt_search_glmapping"
        value={value}
        onChange={onChange}
        placeholder="Search…"
        InputProps={{
          startAdornment: <SearchIcon fontSize="small" />,
          endAdornment: (
            <IconButton
              title="Clear"
              aria-label="Clear"
              size="small"
              style={{
                visibility: value ? 'visible' : 'hidden'
              }}
              onClick={clearSearch}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          ),
        }}
      />
    </FormControl>
  );
}

const TransactionMapper: React.FC = () => {

  const {
    bulkMappingSelection,
    clientId,
    dataGridRows,
    filterModel,
    fundsMapped,
    fundName,
    glConnectionDate,
    glConnectionPlatform,
    glConnectionUsername,
    glPlatform,
    glSetupCurrentIndex,
    glSetupSteps,
    readonly,
    handleCancelGL,
    handleConnectGL,
    handleSelectGL,
    handleDownloadChangeLog,
    handleImportFundForMapping,
    handleNewTransactionSave,
    handleSaveMapping,
    handleSearchTextChange,
    handleSyncGL,
    handleTabChange,
    handleTransTypeChange,
    handleDisconnect,
    handleCancelDisconnect,
    handleConfirmDisconnect,
    hasMappingAdmin,
    isAlertToUnlockOpen,
    isDirty,
    isGLConnected,
    isLoading,
    isModalOpen,
    isScreenLocked,
    isSending,
    isSidePanelLoading,
    isSidePanelOpen,
    onSidePanelClose,
    isReadyToDisconnect,
    progress,
    searchValue,
    selectedFund,
    selectionModel,
    setBulkMappingSelection,
    setFilterModel,
    setIsAlertToUnlockOpen,
    setIsModalOpen,
    setIsScreenLocked,
    setIsSidePanelOpen,
    setSelectedFund,
    setSelectionModel,
    tab,
    transBuckets,
    transOptions,
    transTypes
  } = useTransactionMapperEffect();

  const apiRef = useGridApiRef();
  const columns: GridColumns = [
    {
      field: 'transactionTypeName',
      headerName: 'Ark Transactions',
      editable: true,
      width: 300,
      renderCell: params => renderDataGridSelect(params),
    },
    {
      field: 'dateMapped',
      headerName: 'Date Mapped',
      type: 'string',
      valueFormatter: (params: GridValueFormatterParams) => {
        return params.value ? DateTimeFormat.shortDate(params.value as Date) : '';
      },
      width: 300
    },
    {
      field: 'lastGLActivity',
      headerName: 'Last GL Activity',
      type: 'string',
      valueFormatter: (params: GridValueFormatterParams) => {
        return DateTimeFormat.shortDate(params.value as Date);
      },
      width: 300
    },
  ];

  const groupingColDef: DataGridProProps['groupingColDef'] = {
    headerName: 'GL Accounts',
    filterable: true,
    renderCell: (params) => {
      if (params.row.parentId !== null) {
        return params.row.transactionTypeName === null ?
          <GLAccountChild><Typography>{params.value}</Typography><FiberNewIcon color="success" /></GLAccountChild> :
          <GLAccountChild><Typography>{params.value}</Typography></GLAccountChild>;
      } else {
        return params.row.transactionTypeName === null ?
          <GLAccount><Typography>{params.value}</Typography><FiberNewIcon color="success" /></GLAccount> :
          <GLAccount><Typography>{params.value}</Typography></GLAccount>;
      }
    },
    width: 300
  };

  function renderDataGridSelect(params: GridRenderCellParams<any, any, any>) {
    if (transTypes && transOptions) {
      return (
        <FormControl sx={{
          minWidth: '100%'
        }}>
          <Select
            id={`select_transaction_name_${params.row.id}`}
            labelId={`select_transaction_name_${params.row.id}_label`}
            label="Transaction Name"
            value={params.row.transactionTypeId ? params.row.transactionTypeId : ''}
            onChange={(e) => handleTransTypeChange([params.row.id], e.target.value as string)}
            onClick={(e) => { if (isScreenLocked) { setIsAlertToUnlockOpen(true); } }}
            MenuProps={{
              PaperProps: {
                sx: {
                  padding: '10px',
                  height: '200px',
                  width: '180px',
                  '& .MuiBox-root': {
                    width: '100%'
                  },
                  '& MuiFormControl-root': {
                    minWidth: '100%'
                  }
                }
              }
            }}
            disabled={isScreenLocked}
          >
            <MenuItem sx={{
              minWidth: '100%'
            }} value='0'>Unmapped</MenuItem>
            {transTypes.map(type => {
              return (
                <MenuItem sx={{
                  minWidth: '100%'
                }} key={type.id} value={type.id}>{type.name}</MenuItem>
              );
            }
            )}
            <MenuItem value={''}><TypographyBold>Create New Transaction Type </TypographyBold></MenuItem>
          </Select>
        </FormControl>
      );
    }
  }

  function ImportFundMappingControl() {
    return (
      <SelectBox>
        <Typography variant="body2">Import Mapping</Typography>
        <FormControl size="small">
          <InputLabel id="label_import_fund_select">Select fund</InputLabel>
          <Select
            labelId="select_import_mapping_label"
            id="select_import_fund_mapping"
            label="Select Fund"
            value={selectedFund}
            onChange={e => setSelectedFund(e.target.value)}
            MenuProps={SelectMenuProps}
          >
            {/* TODO: remove hard coded values when more data is available */}
            <MenuItem value="669a9ff4-645a-444a-8533-5506501e2bed">Fund with data 1</MenuItem>
            <MenuItem value="c063e0bd-1edd-4b24-ad3b-6f6e7f321550">Fund with data 2</MenuItem>
            {fundsMapped.map(fund => {
              return (
                <MenuItem key={fund.id} value={fund.id}>{fund.name}__{fund.percentMapped}</MenuItem>
              );
            }
            )}
          </Select>
          <Button
            id="btn_import_fund"
            onClick={handleImportFundForMapping}
            variant="outlined"
            disableElevation
          >
            Import
          </Button>
        </FormControl>
      </SelectBox>
    );
  }

  function BulkMappingControl() {
    return (
      <BulkMappingWrapper>
        <SelectBox>
          <Typography variant="body1">Bulk map selected</Typography>
          <FormControl disabled={!(selectionModel.length > 1)} size="small">
            <InputLabel id="label_bulk_mapping_select">Select Transaction</InputLabel>
            <Select
              labelId="select_bulk_mapping_label"
              id="select_bulk_mapping"
              label="Select Transaction"
              size="small"
              value={bulkMappingSelection}
              onChange={e => setBulkMappingSelection(e.target.value)}
              MenuProps={SelectMenuProps}
            >
              <MenuItem value='0'>Unmapped</MenuItem>
              {transTypes.map(type => {
                return (
                  <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                );
              }
              )}
            </Select>
            <Button
              id="btn_apply_bulk_mapping"
              variant="contained"
              onClick={() => handleTransTypeChange(selectionModel, bulkMappingSelection)}
              disableElevation
            >
              Apply
            </Button>

          </FormControl>
        </SelectBox>
      </BulkMappingWrapper>
    );
  }

  return (
    <Container id="transactionmapper_main" role="main">
      <ProgressModal
        id="modal_transaction_mapping_loading"
        showProgress={isLoading}
      />

      {!readonly && <ProgressModal
        id="modal_saving_mapping"
        showProgress={isSending}
        text="Saving mapping..."
      />}

      {(!isLoading && isGLConnected && dataGridRows && dataGridRows.length > 0) && <>
        <Grid container spacing={2} justifyContent="space-between" alignItems="center">
          <Grid item xs={12}>
            <HeaderRow>
              <Typography variant="h1">Chart of Accounts Mapping</Typography>
              <StyledTooltip
                title={
                  <>
                    <Typography color="inherit">Sync Chart of Accounts list <br />from General Ledger</Typography>
                    <Typography>{glConnectionPlatform} account connected on {glConnectionDate} by {glConnectionUsername}.</Typography>
                    {!readonly && <Typography>
                      <DisconnectButton onClick={handleDisconnect}>
                        Click here
                      </DisconnectButton>
                      to change General Ledger connections.
                    </Typography>}
                  </>
                }
              >
                <StyledIconButton
                  onClick={handleSyncGL}
                  size="large"
                >
                  <AutorenewIcon />
                </StyledIconButton>
              </StyledTooltip>
            </HeaderRow>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between"
              sx={{
                marginBottom: '2em'
              }}
            >
              <LinearProgressWithLabel
                value={(progress.mappedAmount / progress.totalAmount) * 100}
                total={progress.totalAmount}
              />

              <Button
                id="btn_download_changelog"
                onClick={() => handleDownloadChangeLog()}
                disabled={!hasMappingAdmin}
                startIcon={<FileDownloadIcon />}>
                DOWNLOAD CHANGE LOG
              </Button>
            </Stack>
          </Grid>

          {!isScreenLocked && !readonly && (
            <Grid item md={12}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between"
                sx={{
                  marginBottom: '2em'
                }}
              >
                <BulkMappingControl />
                <ImportFundMappingControl />
              </Stack>
            </Grid>
          )}

          <Grid item md={12}>
            <Stack direction="row" spacing={6} alignItems="center" justifyContent="space-between">
              <Box>
                <Tabs id="tabgroup_notmapped_all" value={tab} onChange={handleTabChange} aria-label="not mapped all tabs">
                  <Tab label={`NOT MAPPED ${progress.totalAmount - progress.mappedAmount}`} disabled={progress.mappedAmount === progress.totalAmount} />
                  <Tab label={`ALL ${progress.totalAmount}`} />
                </Tabs>
              </Box>

              <SearchBar
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleSearchTextChange(event.target.value)}
                value={searchValue}
                clearSearch={() => handleSearchTextChange('')}
              />

              {!readonly && <Button
                id="btn_save_mapping"
                color="primary"
                variant="contained"
                onClick={() => handleSaveMapping()}
                disableElevation
                disabled={!hasMappingAdmin || !isDirty || isScreenLocked}
              >Save mapping</Button>}
            </Stack>
          </Grid>

          <Container id="datagrid_transaction_mapper" sx={{
            height: "71vh"
          }}>
            <StyledDataGridPro
              checkboxSelection
              treeData
              getTreeDataPath={row => row.path}
              defaultGroupingExpansionDepth={2}
              apiRef={apiRef}
              rows={dataGridRows}
              columns={columns}
              rowHeight={35}
              groupingColDef={groupingColDef}
              hideFooterRowCount
              hideFooterSelectedRowCount
              filterModel={filterModel}
              onFilterModelChange={(model) => setFilterModel(model)}
              onSelectionModelChange={(newSelectionModel) => { setSelectionModel(newSelectionModel); }}
              selectionModel={selectionModel}
            />
          </Container>

          {!readonly && <PageLock
            locked={isScreenLocked}
            disabled={!hasMappingAdmin}
            onChange={() => setIsScreenLocked(!isScreenLocked)}
          />}
        </Grid >

        {!readonly && <>
          <FullScreenModal
            id="dialog_unlock_transaction_mapping_confirmation"
            open={isAlertToUnlockOpen}
            title="Unlock accounts to start mapping"
            subtitle={undefined}
            confirmButtonText="Unlock"
            cancelButtonText="Cancel"
            onSuccess={() => { setIsScreenLocked(false); setIsAlertToUnlockOpen(false); }}
            onCancel={() => setIsAlertToUnlockOpen(false)}
          />
          <SaveExitModal
            onActionClick={handleSaveMapping}
            isBlocked={isDirty}
            title="Leave Page?"
            content="Your changes have not been saved. Are you sure you want to leave this page?"
            actionTitle="Save Mapping"
            confirmNavigationTitle="Exit Without Saving"
          />
          <NewTransactionModal
            clientId={clientId}
            open={isModalOpen}
            transTypeModel={transBuckets}
            onClose={() => setIsModalOpen(false)}
            onSuccess={handleNewTransactionSave}
          />
        </>
        }
      </>
      }
      {!isGLConnected && !readonly && <>
        <Button
          id="btn_connect_gl"
          color="primary"
          variant="contained"
          onClick={() => setIsSidePanelOpen(true)}
          disableElevation
        > Connect to GL</Button>

      </>}
      <DetailPanel
        id="glsetup_panel"
        open={isSidePanelOpen}
        title={"Connect a General Ledger"}
        onClose={onSidePanelClose}
      >

        <PanelBox>
          <Typography variant="subtitle2">{fundName}</Typography>
          <br />
          <Box>
            <Box sx={{ width: '100%' }}>
              <Stepper activeStep={(glSetupCurrentIndex - 1)} alternativeLabel>
                {glSetupSteps.map((step) => (
                  <Step key={step.label}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            <PanelDescription variant='modalSubtitle'>
              {glSetupSteps[(glSetupCurrentIndex - 1)].description}
            </PanelDescription>

            <TabContext value={glSetupCurrentIndex.toString()}>
              <TabPanel value="1">

                <PlatformButton
                  variant='outlined'
                  onClick={e => handleSelectGL(e)}
                  className={glPlatform === 'qb' ? 'selected' : ''}
                >
                  <img src={QuickBooksLogo} alt="QuickBooks Logo" id="qb" />
                </PlatformButton>
              </TabPanel>
            </TabContext>
          </Box>

        </PanelBox>

        <StyledBackdrop
          open={isSidePanelLoading}
        />
        <ProgressPanel
          id={"progress_glsetup_panel"}
          showProgress={isSidePanelLoading}
          text="Redirecting to QuickBooks..."
        />

        <HorizontalBox
          addTopShadow
          attachToBottom={true}
          hidden={false}
          fullWidth
        >
          <StyledButton
            id={"btn_glsetup_panel_cancel"}
            variant="outlined"
            onClick={handleCancelGL}
            text={"Cancel"}
            color={"secondary"}
            fullWidth
            addSpaceBetweenButtons
          />

          <StyledButton
            id={"btn_glsetup_panel_connect"}
            variant="contained"
            onClick={handleConnectGL}
            text={"Connect"}
            color={"primary"}
            fullWidth
            addSpaceBetweenButtons
            disabled={!glPlatform}
          />
        </HorizontalBox>
      </DetailPanel>
      <ConfirmationDialog
        open={isReadyToDisconnect}
        onClose={handleCancelDisconnect}
        id="disconnect_gl_confirmation"
        actions={[
          {
            label: "Disconnect",
            onClick: () => handleConfirmDisconnect(),
            id: "btn_confirm_disconnect_gl",
            variant: "contained",
            color: "primary",
          },
          {
            label: "Cancel",
            onClick: handleCancelDisconnect,
            id: "btn_cancel_disconnect",
            variant: "outlined",
            color: "error",
          },
        ]}
        content="Are you sure you want to disconnect General Ledger? This action cannot be undone."
        title="Disconnect General Ledger?"
      />

    </Container>
  );
};

export default TransactionMapper;
